"use client";

import { cn } from "@/lib/utils";
import type { HeroSection, HeroSlide } from "@/ui/yns-components/components/hero-section/hero-section";
import type { YnsThemeType } from "@/ui/yns-components/components/types";
import { YnsLink } from "@/ui/yns-link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

const TIME = 5000;

declare module "csstype" {
	interface Properties {
		"--time"?: `${number}ms`;
	}
}

export const HeroSectionInner = ({
	slides,
	size = "fixed-width",
	theme,
}: {
	slides: HeroSlide[];
	size?: "full-width" | "fixed-width";
	/**
	 * @deprecated
	 */
	theme?: YnsThemeType<typeof HeroSection>;
}) => {
	const [currentSlide, setCurrentSlide] = useState(0);

	// undefined = not started
	// null = manually stopped (on click)
	// number = running
	const intervalRef = useRef<ReturnType<typeof setInterval> | null | undefined>(undefined);

	useEffect(() => {
		intervalRef.current = setInterval(() => {
			setCurrentSlide((prev) => (prev + 1) % slides.length);
		}, TIME);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [slides.length]);

	return (
		<div>
			{slides.map((data, idx) => {
				return (
					<section
						key={idx}
						className={cn(
							`rounded-lg overflow-hidden flex items-center absolute inset-0 transition-opacity duration-1000`,
							currentSlide === idx ? "opacity-100" : "opacity-0 pointer-events-none",
							data.boxAlignment === "left" && "justify-start",
							data.boxAlignment === "center" && "justify-center",
							data.boxAlignment === "right" && "justify-end",

							data.textAlignment === "left" && "text-left",
							data.textAlignment === "center" && "text-center",
							data.textAlignment === "right" && "text-right",
							size === "full-width" && "items-stretch rounded-none",
						)}
					>
						<div
							className={cn(
								"w-1/2 space-y-4 text-(--theme-primary-color) p-8 lg:p-12 z-10 relative flex flex-col items-end justify-center",
								data.textAlignment === "left" && "items-start",
								data.textAlignment === "center" && "items-center",
								data.textAlignment === "right" && "items-end",

								size === "fixed-width" && data.boxAlignment === "left" && "rounded-r-sm",
								size === "fixed-width" && data.boxAlignment === "center" && "rounded-sm",
								size === "fixed-width" && data.boxAlignment === "right" && "rounded-l-sm",
							)}
							style={{
								backgroundColor: data.theme?.backgroundColor || theme?.backgroundColor || undefined,
								color: data.theme?.color || theme?.color || undefined,
							}}
						>
							<h2
								className="text-balance text-3xl font-bold tracking-tight md:text-4xl"
								data-yns-field="title"
							>
								{data.title}
							</h2>
							<p data-yns-field="description" className="text-pretty">
								{data.description}
							</p>
							{data.button?.label && (
								<YnsLink
									className={cn(
										"inline-flex h-10 items-center justify-center rounded-full px-6 font-medium text-(--theme-button-color) transition-colors",
										"focus:outline-hidden focus:ring-1 focus:ring-(--theme-button-color)",
										"bg-[var(--theme-hero-section-cta-background-color,#222)]",
										"text-[var(--theme-hero-section-cta-color,#EEE)]",
										"hover:bg-[var(--theme-hero-section-cta-hover-background-color,#555)]",
									)}
									style={{
										"--theme-hero-section-cta-background-color":
											data.theme?.buttonBackgroundColor || theme?.buttonBackgroundColor || undefined,
										"--theme-hero-section-cta-color":
											data.theme?.buttonTextColor || theme?.buttonTextColor || undefined,
										"--theme-hero-section-cta-hover-background-color":
											data.theme?.buttonHoverBackgroundColor ||
											theme?.buttonHoverBackgroundColor ||
											undefined,
									}}
									href={data.button.path ?? "#"}
								>
									{data.button.label}
								</YnsLink>
							)}
						</div>
						{data.image?.src && (
							<Image
								alt={data.image.alt}
								loading="eager"
								priority={true}
								fetchPriority="high"
								className="rounded absolute top-0 left-0 object-cover w-full h-full"
								width={1280}
								height={720}
								src={data.image.src}
								style={{
									objectFit: "cover",
									zIndex: "-1",
								}}
								sizes="(max-width: 1280px) 100vw, 1280px"
							/>
						)}
					</section>
				);
			})}

			{/* Indicators */}

			{slides.length > 1 && (
				<div
					role="tablist"
					className="z-10 flex flex-row gap-2 absolute bottom-6 left-1/2 -translate-x-1/2 backdrop-blur-sm rounded-sm px-2 py-1"
					style={{
						"--time": `${TIME}ms`,
					}}
				>
					{slides.map((slide, idx) => (
						<button
							key={idx}
							type="button"
							className={cn(
								"h-1.5 w-2.5 rounded-full bg-neutral-200 cursor-pointer transform-gpu will-change-[width] transition-[width] duration-700",
								"before:content-[''] before:block before:rounded-full before:transform-gpu will-change-[width] before:transition-[width,opacity] before:duration-700 before:opacity-0 before:bg-black before:w-0 before:h-full",
								currentSlide === idx &&
									"w-12 transition-[width] before:w-full before:transition-[width,opacity] before:duration-[var(--time),700ms] before:ease-linear before:starting:w-0 before:starting:opacity-0 before:opacity-100",

								// fast transition after click
								currentSlide === idx && intervalRef.current === null && "before:duration-[300ms]",
							)}
							aria-selected={currentSlide === idx}
							aria-busy={Boolean(currentSlide === idx && intervalRef.current)}
							aria-controls={`testimonial-${idx}`}
							onClick={() => {
								if (intervalRef.current) {
									clearInterval(intervalRef.current);
									intervalRef.current = null;
								}
								setCurrentSlide(idx);
							}}
						>
							<span className="sr-only">Slide {idx + 1}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
};
